import { z } from 'zod';

import { blockSchema, safeHref } from './blocks.ts';

const text = (max: number) => z.string().trim().max(max);

export const seoSchema = z.object({
  title: text(70).min(1),
  description: text(170).min(1),
  robots: z.enum(['index', 'noindex']).default('index'),
  ogImageUrl: safeHref.optional(),
});
export type Seo = z.infer<typeof seoSchema>;

export const landingContentSchema = z.object({
  blocks: z.array(blockSchema).min(1).max(30),
  seo: seoSchema,
});
export type LandingContent = z.infer<typeof landingContentSchema>;

/** A draft may be incomplete; publishing validates the full schema plus form/route rules. */
export const publishRequestSchema = z.object({ expectedRevision: z.number().int().min(0) });

export const landingDraftSaveSchema = z.object({
  content: landingContentSchema,
  expectedRevision: z.number().int().min(0),
});
export type LandingDraftSave = z.infer<typeof landingDraftSaveSchema>;

export const landingVariantSummarySchema = z.object({
  id: z.string(),
  landingId: z.string(),
  key: z.string(),
  name: z.string(),
  locale: z.string(),
  slug: z.string(),
  draftRevision: z.number().int(),
  hasUnpublishedChanges: z.boolean(),
  publishedVersionNumber: z.number().int().nullable(),
  updatedAt: z.string(),
});
export type LandingVariantSummary = z.infer<typeof landingVariantSummarySchema>;

export const landingDraftSchema = z.object({
  variantId: z.string(),
  landingId: z.string().optional(),
  name: z.string(),
  kind: z.string().optional(),
  locale: z.string(),
  slug: z.string(),
  /** Public path of the page (locale prefix applied). */
  publicPath: z.string().optional(),
  revision: z.number().int(),
  content: landingContentSchema,
  updatedAt: z.string(),
  publishedVersionNumber: z.number().int().nullable(),
  state: z.string().optional(),
  translationStatus: z.string().optional(),
  scheduledFor: z.string().nullable().optional(),
  scheduleError: z.string().nullable().optional(),
  restrictedCategory: z.boolean().optional(),
  siblings: z.array(z.object({ variantId: z.string(), locale: z.string(), slug: z.string(), translationStatus: z.string(), published: z.boolean() })).optional(),
});
export type LandingDraft = z.infer<typeof landingDraftSchema>;

export const publishResultSchema = z.object({
  versionId: z.string(),
  versionNumber: z.number().int(),
  publishedAt: z.string(),
  publicPath: z.string(),
});
export type PublishResult = z.infer<typeof publishResultSchema>;

/** What the public renderer receives for a published page. */
export const publishedLandingSchema = z.object({
  versionId: z.string(),
  versionNumber: z.number().int(),
  variantId: z.string(),
  locale: z.string(),
  slug: z.string(),
  content: landingContentSchema,
  form: z.lazy(() => formDefinitionSchema),
  formVersionId: z.string(),
  contextToken: z.string(),
  publishedAt: z.string(),
  /** Page facts for rendering and SEO, computed once by the API so every consumer agrees. */
  name: z.string().optional(),
  kind: z.string().optional(),
  canonical: z.string().optional(),
  alternates: z.array(z.object({ locale: z.string(), hreflang: z.string(), path: z.string() })).optional(),
  jsonLd: z.array(z.record(z.string(), z.unknown())).optional(),
  breadcrumbs: z.array(z.object({ name: z.string(), path: z.string() })).optional(),
  /** Resolved data for connected blocks (the renderer never fetches). */
  context: z
    .object({
      brand: z.object({ name: z.string().optional(), email: z.string().optional(), phone: z.string().optional(), address: z.string().optional() }).optional(),
      services: z.array(z.object({ key: z.string(), name: z.string(), description: z.string(), href: z.string() })).default([]),
      articles: z.array(z.object({ title: z.string(), href: z.string(), summary: z.string().optional(), publishedAt: z.string().optional() })).default([]),
      caseStudies: z.array(z.object({ slug: z.string(), title: z.string(), summary: z.string(), href: z.string(), industry: z.string().optional(), coverImageUrl: z.string().optional() })).default([]),
    })
    .optional(),
});
export type PublishedLanding = z.infer<typeof publishedLandingSchema>;

import { formDefinitionSchema } from './form.ts';
